'use client';

import { useState, useEffect, useRef, memo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Shield, Star, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { useRTL } from '@/hooks/useRTL';
import { ensureStringArray } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

const AnimatedPrice = memo(function AnimatedPrice({
    value,
    className = "",
    prefix = "$",
    locale
}: {
    value: number;
    className?: string;
    prefix?: string;
    locale: string;
}) {
    const [displayValue, setDisplayValue] = useState(0);
    const hasAnimatedRef = useRef(false);
    const elementRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        let rafId: number;
        hasAnimatedRef.current = false;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimatedRef.current) {
                    hasAnimatedRef.current = true;

                    let startTime: number;
                    const animate = (timestamp: number) => {
                        if (!startTime) startTime = timestamp;
                        const progress = Math.min((timestamp - startTime) / 1500, 1);

                        const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
                        const currentValue = Math.floor(easeOutExpo * value);

                        setDisplayValue(currentValue);

                        if (progress < 1) {
                            rafId = requestAnimationFrame(animate);
                        }
                    };

                    rafId = requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, [value]);

    const formatNumber = (num: number) => {
        const formatted = num.toLocaleString(locale);
        if (locale === 'ar') {
            return formatted.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
        } else if (locale === 'fa') {
            return formatted.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d)]);
        }
        return formatted;
    };

    return (
        <span ref={elementRef} className={className}>
            {prefix}{formatNumber(displayValue)}
        </span>
    );
});

export default function PricingSection() {
    const { t, locale } = useTranslation('root-canal');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const comparisonRef = useRef<HTMLDivElement>(null);

    const handleConsultationClick = () => {
        window.open('/book-appointment', '_blank');
    };


    const priceComparison = [
        {
            location: t('pricing.priceComparison.us.location'),
            flag: "🇺🇸",
            price: 1750,
            range: t('pricing.priceComparison.us.range'),
            excludes: t('pricing.priceComparison.us.excludes'),
            quality: t('pricing.priceComparison.us.quality'),
            waitTime: t('pricing.priceComparison.us.waitTime'),
            color: "from-blue-500 to-blue-600",
            included: ensureStringArray(t('pricing.priceComparison.us.included', { returnObjects: true })),
            additional: ensureStringArray(t('pricing.priceComparison.us.additional', { returnObjects: true }))
        },
        {
            location: t('pricing.priceComparison.uk.location'),
            flag: "🇬🇧",
            price: 1300,
            range: t('pricing.priceComparison.uk.range'),
            excludes: t('pricing.priceComparison.uk.excludes'),
            quality: t('pricing.priceComparison.uk.quality'),
            waitTime: t('pricing.priceComparison.uk.waitTime'),
            color: "from-purple-500 to-purple-600",
            included: ensureStringArray(t('pricing.priceComparison.uk.included', { returnObjects: true })),
            additional: ensureStringArray(t('pricing.priceComparison.uk.additional', { returnObjects: true }))
        },
        {
            location: t('pricing.priceComparison.eu.location'),
            flag: "🇪🇺",
            price: 1100,
            range: t('pricing.priceComparison.eu.range'),
            excludes: t('pricing.priceComparison.eu.excludes'),
            quality: t('pricing.priceComparison.eu.quality'),
            waitTime: t('pricing.priceComparison.eu.waitTime'),
            color: "from-green-500 to-green-600",
            included: ensureStringArray(t('pricing.priceComparison.eu.included', { returnObjects: true })),
            additional: ensureStringArray(t('pricing.priceComparison.eu.additional', { returnObjects: true }))
        },
        {
            location: t('pricing.priceComparison.dentawista.location'),
            flag: "🇹🇷",
            price: 375,
            range: t('pricing.priceComparison.dentawista.range'),
            includes: t('pricing.priceComparison.dentawista.includes'),
            quality: t('pricing.priceComparison.dentawista.quality'),
            waitTime: t('pricing.priceComparison.dentawista.waitTime'),
            color: "from-gold to-gold-600",
            savings: t('pricing.priceComparison.dentawista.savings'),
            featured: true,
            included: ensureStringArray(t('pricing.priceComparison.dentawista.included', { returnObjects: true }))
        }
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 90%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            // Cards animation - individual triggers for each card
            priceComparison.forEach((_, index) => {
                const cardElement = cardsRef.current?.children[index];
                if (cardElement) {
                    gsap.fromTo(cardElement,
                        { opacity: 0, y: 40, scale: 0.95 },
                        {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            duration: 0.6,
                            ease: "power2.out",
                            scrollTrigger: {
                                trigger: cardElement,
                                start: "top 95%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                }
            });

            // Comparison animation
            gsap.fromTo(comparisonRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: comparisonRef.current,
                        start: "top 90%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

        }, sectionRef);

        return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Empty deps is intentional: DOM structure and
    // priceComparison are stable for the component lifetime (translations only affect initial render).
    // Animations must only initialize once to avoid re-triggering on re-renders.
    }, []);

    return (
        <section id="pricing" ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif mb-6">
                        {t('pricing.title.part1')} <span className="text-gold">{t('pricing.title.part2')}</span>
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
                        {t('pricing.subtitle')}
                    </p>

                    {/* No Hidden Fees Badge */}
                    <div className={`inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-6 py-3 text-gold font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Shield className="w-5 h-5" />
                        <span>{t('pricing.guaranteeBadge')}</span>
                        <CheckCircle className="w-5 h-5" />
                    </div>
                </div>

                {/* Price Comparison Cards */}
                <div ref={cardsRef} className="flex flex-wrap justify-center gap-6 mb-12">
                    {priceComparison.map((item, index) => (
                        <div
                            key={index}
                            className={`relative overflow-visible rounded-2xl border ${item.featured
                                ? 'border-2 border-gold shadow-xl shadow-gold/20 hover:shadow-2xl hover:shadow-gold/30'
                                : 'border-slate-200 dark:border-slate-700 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600'
                                } bg-white dark:bg-slate-800 transition-all duration-300 transform hover:-translate-y-2 w-full max-w-xs group`}
                        >
                            {item.featured && (
                                <div className="absolute -top-2 -right-2 z-30">
                                    <div className="relative">
                                        {/* Main ribbon banner */}
                                        <div className="bg-gradient-to-br from-red-600 via-red-500 to-red-700 text-white px-4 py-2 text-xs font-bold shadow-2xl relative min-w-[70px] rounded-sm">
                                            <div className="text-center leading-tight">
                                                <div className="tracking-wide">{t('pricing.bestValueBadge.line1')}</div>
                                                <div className="tracking-wide">{t('pricing.bestValueBadge.line2')}</div>
                                            </div>

                                            {/* Decorative stitching lines */}
                                            <div className="absolute left-1 top-1 bottom-1 w-px bg-white/40"></div>
                                            <div className="absolute right-1 top-1 bottom-1 w-px bg-white/40"></div>

                                            {/* Top border highlight */}
                                            <div className="absolute top-0 left-0 right-0 h-px bg-white/30"></div>

                                            {/* Bottom ribbon tails */}
                                            <div className="absolute -bottom-2 left-0 w-0 h-0 border-l-[17px] border-l-red-700 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent"></div>
                                            <div className="absolute -bottom-2 right-0 w-0 h-0 border-r-[17px] border-r-red-700 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent"></div>

                                            {/* Center notch for flag effect */}
                                            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-red-800"></div>
                                        </div>

                                        {/* Ribbon shadow */}
                                        <div className="absolute top-1 left-1 right-1 bottom-1 bg-black/20 rounded-sm -z-10"></div>
                                    </div>
                                </div>
                            )}

                            <div className={`p-6 ${item.featured ? 'pt-8' : ''}`}>
                                {/* Header */}
                                <div className="text-center mb-4">
                                    <div className="text-3xl mb-2">{item.flag}</div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                                        {item.location}
                                    </h3>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        {item.range}
                                    </div>
                                    {item.excludes && (
                                        <div className="text-xs text-red-500 mt-1">
                                            ({t('pricing.labels.excluding')} {item.excludes})
                                        </div>
                                    )}
                                    {item.includes && (
                                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                            ({item.includes})
                                        </div>
                                    )}
                                </div>

                                {/* Price */}
                                <div className="text-center mb-4 min-h-[60px] flex flex-col justify-center">
                                    <AnimatedPrice
                                        value={item.price}
                                        prefix={
                                            item.location === "United Kingdom" ? "£" :
                                                item.location === "European Union" ? "€" :
                                                    item.location === "DentaWista Turkey" ? "$" :
                                                        "$"
                                        }
                                        className={`text-2xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent transition-all duration-300 ${item.featured ? 'group-hover:text-3xl' : ''}`}
                                        locale={locale}
                                    />
                                    {item.savings && (
                                        <div className="text-sm text-green-600 dark:text-green-400 font-semibold mt-1 transition-all duration-300 group-hover:text-base">
                                            {item.savings}
                                        </div>
                                    )}
                                </div>

                                {/* Quality & Wait Time */}
                                <div className={`flex justify-between text-sm mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <div className={`flex gap-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="font-semibold text-gray-700 dark:text-gray-300">{t('pricing.labels.quality')}</span>
                                        <span className="text-gray-600 dark:text-gray-400">{item.quality}</span>
                                    </div>
                                    <div className={`flex gap-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="font-semibold text-gray-700 dark:text-gray-300">{t('pricing.labels.wait')}</span>
                                        <span className="text-gray-600 dark:text-gray-400">{item.waitTime}</span>
                                    </div>
                                </div>

                                {/* What's Included */}
                                <div className="mb-4">
                                    <h4 className={`text-sm font-semibold mb-2 text-green-600 dark:text-green-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('pricing.labels.included')}
                                    </h4>
                                    <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
                                        {item.included.map((service: string, idx: number) => (
                                            <li key={idx} className={`flex items-start gap-1 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                                <span className="flex-1">{service}</span>
                                                <span className="text-green-500 flex-shrink-0">✓</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Additional Services */}
                                    {item.additional && item.additional.length > 0 && (
                                        <>
                                            <h4 className={`text-sm font-semibold mb-2 text-red-600 dark:text-red-400 mt-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                                                {t('pricing.labels.notIncluded')}
                                            </h4>
                                            <ul className="space-y-1 text-xs text-red-600 dark:text-red-400">
                                                {item.additional.map((service: string, idx: number) => (
                                                    <li key={idx} className={`flex items-start gap-1 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                                        <span className="flex-1">{service}</span>
                                                        <span className="text-red-500 flex-shrink-0">×</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>

                                {/* Add CTA for featured card */}
                                {item.featured && (
                                    <div className="mt-4 pt-4 border-t border-gold/20">
                                        <Button
                                            onClick={handleConsultationClick}
                                            className="w-full bg-gold hover:bg-gold-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 group-hover:shadow-lg"
                                        >
                                            {t('pricing.labels.consultationButton')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Comparison Table */}
                <div ref={comparisonRef} className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-700">
                    <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                            <span className="text-gold">{t('pricing.comparison.title.part1')}</span> {t('pricing.comparison.title.part2')}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                            {t('pricing.comparison.subtitle')}
                        </p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                                    <th className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.headers.feature')}</th>
                                    <th className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.headers.traditional')}</th>
                                    <th className={`py-4 px-4 font-semibold text-gold ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.headers.dentawista')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                <tr>
                                    <td className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.rows.cost.feature')}</td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-red-600 dark:text-red-400 font-semibold">{t('pricing.comparison.table.rows.cost.traditional.label')}</span> {t('pricing.comparison.table.rows.cost.traditional.description')}
                                    </td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-green-600 dark:text-green-400 font-semibold">{t('pricing.comparison.table.rows.cost.dentawista.label')}</span> {t('pricing.comparison.table.rows.cost.dentawista.description')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.rows.pricing.feature')}</td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-red-600 dark:text-red-400 font-semibold">{t('pricing.comparison.table.rows.pricing.traditional.label')}</span> {t('pricing.comparison.table.rows.pricing.traditional.description')}
                                    </td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-green-600 dark:text-green-400 font-semibold">{t('pricing.comparison.table.rows.pricing.dentawista.label')}</span> {t('pricing.comparison.table.rows.pricing.dentawista.description')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.rows.waiting.feature')}</td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-red-600 dark:text-red-400 font-semibold">{t('pricing.comparison.table.rows.waiting.traditional.label')}</span> {t('pricing.comparison.table.rows.waiting.traditional.description')}
                                    </td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-green-600 dark:text-green-400 font-semibold">{t('pricing.comparison.table.rows.waiting.dentawista.label')}</span> {t('pricing.comparison.table.rows.waiting.dentawista.description')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.rows.experience.feature')}</td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-red-600 dark:text-red-400 font-semibold">{t('pricing.comparison.table.rows.experience.traditional.label')}</span> {t('pricing.comparison.table.rows.experience.traditional.description')}
                                    </td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-green-600 dark:text-green-400 font-semibold">{t('pricing.comparison.table.rows.experience.dentawista.label')}</span> {t('pricing.comparison.table.rows.experience.dentawista.description')}
                                    </td>
                                </tr>
                                <tr>
                                    <td className={`py-4 px-4 font-semibold text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{t('pricing.comparison.table.rows.accessibility.feature')}</td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-red-600 dark:text-red-400 font-semibold">{t('pricing.comparison.table.rows.accessibility.traditional.label')}</span> {t('pricing.comparison.table.rows.accessibility.traditional.description')}
                                    </td>
                                    <td className={`py-4 px-4 text-gray-600 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-green-600 dark:text-green-400 font-semibold">{t('pricing.comparison.table.rows.accessibility.dentawista.label')}</span> {t('pricing.comparison.table.rows.accessibility.dentawista.description')}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    );
} 